import { message } from 'antd';

/**
 * Error shape returned by the request funnel (`createDocumentRequest` /
 * `getDocuments`) after the de-silencing work (MV2-1164/1214). On success the
 * funnel unwraps to the plain data; on failure it returns this object.
 */
export interface ApiError {
  type: 'error';
  status: number | null;
  data?: unknown;
}

const GENERIC_MESSAGE = 'Falha ao carregar os dados. Tente novamente.';

const MESSAGES_BY_STATUS: Record<number, string> = {
  400: 'Requisição inválida.',
  403: 'Você não tem permissão para acessar este recurso.',
  404: 'Recurso não encontrado.',
  429: 'Muitas requisições. Tente novamente em instantes.',
};

/**
 * Maps an HTTP status (or `null` for network/exception failures) to a
 * user-facing message. Unknown statuses fall back to a generic message.
 */
export function messageForStatus(status: number | null): string {
  if (status === null) return GENERIC_MESSAGE;
  return MESSAGES_BY_STATUS[status] ?? GENERIC_MESSAGE;
}

function isApiError(result: unknown): result is ApiError {
  return (
    typeof result === 'object' &&
    result !== null &&
    (result as ApiError).type === 'error'
  );
}

/**
 * Boundary helper for consuming the request funnel result. If `result` is an
 * error object, shows a toast to the user and returns `fallback` (a fail-safe
 * value for the caller's type, e.g. `[]` / `{}` / `null`); otherwise returns
 * the result untouched.
 *
 * Exception: status 403 returns the fallback WITHOUT a toast. Decided after the
 * production rollout — in this API a 403 also shows up in legitimate, routine
 * scenarios (plan limit reached, user outside the operator's scope), so the
 * toast was noise. The feedback for a 403 is left to the screen, through the
 * fallback shape plus the consumer's own guards.
 */
export function unwrapOrToast<T>(result: unknown, fallback: T): T {
  if (isApiError(result)) {
    if (result.status === 403) return fallback;

    message.error(messageForStatus(result.status));
    return fallback;
  }
  return result as T;
}
